import { MasterViewModel } from '../../../base-classes/master-view-model';
import { HttpClient } from '@angular/common/http';
import { Package, ClientTenant, Client, Tenant } from '../models';
import { FuntionalAreaService } from '../services/test-suite.service';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { MessageHelperService } from '../../../base-classes/master-message-service';
import { signal, computed } from '@angular/core';
import { ScenarioService } from '../../scenario/services/scenario.service';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/toast/toast.service';
import { PackagesService } from '../../packages/services/packages.service';
import { ClientService } from '../../client/services/client.service';
import { AuthService } from '../../../auth/login/services/auth.service';

export class TestSuiteViewModel extends MasterViewModel<Package> {
  functionalAreaDialog: boolean = false;
  funtionalAreas = signal<any[]>([]);
  selectedFunctionalArea: Package | null = null;
  isEditMode: boolean = false;
  isViewMode: boolean = false;
  type_test = signal<any[]>([]);
  packages: any[] = [];
  clientFilterOptions = signal<{ label: string, value: string }[]>([]);
  selectedClientId = signal<string | null>(null);
  isClientDropdownDisabled = signal<boolean>(false);
  selectedTestTypeFilter = signal<string | null>(null);

  testTypeFilterOptions = computed(() => {
    return this.type_test().map(type => ({
      label: type.referenceValue,
      value: type.id
    }));
  });

  filteredFunctionalAreas = computed(() => {
    const clientId = this.selectedClientId();
    const testTypeId = this.selectedTestTypeFilter();
    const areas = this.funtionalAreas();

    // First filter by client
    let filtered: any[] = [];
    if (!clientId) {
      if (this.getIsSystemUser()) {
        filtered = areas.filter(area => !area.clientPackageMapping);
      } else {
        filtered = [];
      }
    } else if (clientId === 'default') {
      filtered = areas.filter(area => !area.clientPackageMapping);
    } else {
      filtered = areas.filter(area => area.clientPackageMapping?.clientId === clientId);
    }

    // Then filter by test type if selected
    if (testTypeId) {
      filtered = filtered.filter(area => area.testType?.id === testTypeId);
    }

    return filtered;
  });

  constructor(
    http: HttpClient,
    private messageService: ToastService,
    private confirmationService: ConfirmationService,
    private funtionalAreaService: FuntionalAreaService,
    private scenarioService: ScenarioService,
    private router: Router,
    private packagesService: PackagesService,
    private clientService: ClientService,
    private authService: AuthService,
  ) {

    const baseUrl = `${environment.apiUrl}/functional-areas`;
    const moduleName = 'Functional Area';
    super(http, baseUrl, moduleName);
    this.createFunctionalAreaForm();
  }

  public init(): void {
    this.getAllFunctionalAreas();
    this.loadTestTypes();
    this.packageSelectOptions();
    this.loadClients();
    this.clearAll()
  }

  clearAll(): void {
    sessionStorage.removeItem('scenario_functionalAreaId');
    sessionStorage.removeItem('scenario_testTypeId');
    sessionStorage.removeItem('scenario_clientId');
  }

  private loadClients(): void {
    if (this.clientFilterOptions().length > 0) {
      return;
    }

    this.clientService.getMyClients().subscribe({
      next: (res: any) => {
        const clientData = res.data || res;
        const mappedClients = clientData.map((client: any) => ({
          label: client.name,
          value: client.id
        }));

        const isSystemUser = this.isSystemUser();

        if (isSystemUser) {
          this.clientFilterOptions.set([
            { label: 'Master Client', value: 'default' },
            ...mappedClients
          ]);
          this.isClientDropdownDisabled.set(false);
          this.selectedClientId.set('default');
        } else {
          this.clientFilterOptions.set(mappedClients);

          if (mappedClients.length === 1) {
            this.selectedClientId.set(mappedClients[0].value);
            this.isClientDropdownDisabled.set(true);
          } else {
            this.isClientDropdownDisabled.set(false);
          }
        }
      },
      error: (error: any) => {
        this.messageService.error('Unable to load clients');
      }
    });
  }

  public onClientChange(event: any): void {
    this.selectedClientId.set(event.value);
    this.selectedTestTypeFilter.set(null);
  }

  public clearClientFilter(): void {
    if (this.isSystemUser()) {
      this.selectedClientId.set('default');
      this.selectedTestTypeFilter.set(null);
      return;
    }

    if (this.isClientDropdownDisabled()) {
      return;
    }

    this.selectedClientId.set(null);
    this.selectedTestTypeFilter.set(null);
  }

  public filterByTestType(testTypeId: string | null): void {
    this.selectedTestTypeFilter.set(testTypeId);
  }

  public clearTestTypeFilter(): void {
    this.selectedTestTypeFilter.set(null);
  }

  public getIsSystemUser(): boolean {
    return this.isSystemUser();
  }

  isSystemUser(): boolean {
    const userInfo = this.authService.getUserInfoFromToken();
    return userInfo?.isSystemUser === true;
  }

  private createFunctionalAreaForm(): void {
    this.createForm({
      packageId: [null, [this.validators.required]],
      testTypeId: [null, [this.validators.required]],
      name: ['', [this.validators.required, this.validators.maxLength(255)]],
      description: ['', [this.validators.maxLength(255)]]
    });
  }

  private loadTestTypes(): void {
    if (this.type_test().length > 0) {
      return;
    }
    this.scenarioService.getReferenceList(['TEST_TYPE']).subscribe({
      next: (res: any) => {
        const dataResult = res.data || res;
        dataResult.map((type: any) => {
          type.label = type.referenceValue,
            type.value = type.id
        });
        this.type_test.set(dataResult || []);
      },
      error: () => { }
    });
  }


  public packageSelectOptions() {
    if (this.packages.length > 0) {
      return;
    }
    this.packagesService.getAll().subscribe({
      next: (res: any) => {
        const packagesData = res.data || res;
        packagesData.map((pkg: any) => {
          pkg.label = pkg.packageName,
            pkg.value = pkg.id
        });
        this.packages = packagesData || [];
      },
      error: (error: any) => { }
    });
  }

  public getAllFunctionalAreas(): void {
    this.funtionalAreaService.getAllFunctionalAreas().subscribe({
      next: (res: any) => {
        const functionalAreasData = res.data || res;
        this.funtionalAreas.set(functionalAreasData || []);
      },
      error: (error: any) => {
        this.messageService.error('Unable to get packages list');
      }
    });
  }

  public openNew(): void {
    this.selectedFunctionalArea = null;
    this.isEditMode = false;
    this.isViewMode = false;
    this.functionalAreaDialog = true;
    this.form.reset();
    this.form.get('packageId')?.setValidators([this.validators.required]);
    this.form.get('packageId')?.updateValueAndValidity();
    this.form.get('packageId')?.enable();
    this.form.get('testTypeId')?.enable();
  }


  public viewFunctionalArea(data: any): void {
    this.selectedFunctionalArea = data;
    this.isEditMode = false;
    this.isViewMode = true;
    this.functionalAreaDialog = true;

    this.form.patchValue({
      name: data.name,
      description: data.description,
      testTypeId: data?.testType?.id,
      packageId: data?.package?.id
    });

    this.form.disable();
  }

  public editFunctionalArea(data: any): void {
    this.selectedFunctionalArea = data;
    this.isEditMode = true;
    this.isViewMode = false;
    this.functionalAreaDialog = true;
    this.form.patchValue({
      name: data.name,
      description: data.description,
      testTypeId: data?.testType?.id,
      packageId: data?.package?.id
    });
    this.form.get('packageId')?.disable();
    this.form.get('testTypeId')?.disable();
  }

  public saveFunctionalArea(): void {
    if (!this.validateForm()) return;

    const formValue = this.form.getRawValue();

    const payload: any = {
      "packageId": formValue.packageId,
      "name": formValue.name,
      "testTypeId": formValue.testTypeId,
      "description": formValue.description,
    };

    if (this.isEditMode && this.selectedFunctionalArea) {
      payload.id = this.selectedFunctionalArea.id;
    }

    if (this.isEditMode && this.selectedFunctionalArea) {
      this.funtionalAreaService.save(payload).subscribe({
        next: () => {
          this.messageService.success('Functional Area updated successfully');
          this.hideDialog();
          this.getAllFunctionalAreas();
        },
        error: (error: any) => {
          this.messageService.error(error.error?.message || 'Failed to update functional area');
        }
      });
    } else {
      this.funtionalAreaService.save(payload).subscribe({
        next: () => {
          this.messageService.success('Functional Area created successfully');
          this.hideDialog();
          this.getAllFunctionalAreas();
        },
        error: (error: any) => {
          this.messageService.error(error.error?.message || 'Failed to create functional area');
        }
      });
    }
  }

  public deleteFunctionalArea(data: any): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the functional area ?`,
      header: 'Confirm',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-warn',
      accept: () => {

        this.funtionalAreaService.deleteFunctionalArea(data.id).subscribe({
          next: () => {
            this.messageService.success('Functional Area deleted successfully');
            this.getAllFunctionalAreas();
          },
          error: (error: any) => {
            this.messageService.error(error.error?.message || 'Failed to delete functional area');
          }
        });
      }
    });
  }

  public hideDialog(): void {
    this.selectedFunctionalArea = null;
    this.functionalAreaDialog = false;
    this.isEditMode = false;
    this.isViewMode = false;
    this.form.reset();
    this.form.enable();
  }

  public onGlobalFilter(table: Table, event: Event): void {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }
}